import { useMemo, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { METERS_REQUIRING_ESTIMATES, SAVE_ESTIMATE_BOOKINGS } from 'graphql/metersRequiringEstimates.gql';
import { ESTIMATION_FACTORS } from 'graphql/estimationFactors.gql';
import type { MeterEstimateData, RegionalRow } from './EstimationBlade';

interface MeterGroup {
  groupKey: string;
  meters: MeterEstimateData[];
}

interface EstimationFactorRow {
  id: number;
  energy_source: string;
  month_year: string;
  region: string;
  estimate_factor: number | null;
  prev_month_net_units: number | null;
  current_month_net_units: number | null;
}

interface UseMeterIdInfo {
  meter_id: string | number;
  meter_number: string | null;
}

export function useEstimationBlade({
  open,
  meter,
  monthYear,
  onSuccess,
}: {
  open: boolean;
  meter: UseMeterIdInfo | null;
  monthYear: string;
  onSuccess?: () => void;
}) {
  const [applyError, setApplyError] = useState<string | null>(null);

  const { data: metersData } = useQuery(METERS_REQUIRING_ESTIMATES, {
    variables: { month_year: monthYear },
    skip: !open || !meter,
    fetchPolicy: 'no-cache',
  });

  const { data: factorsData } = useQuery(ESTIMATION_FACTORS, {
    variables: { month_year: monthYear },
    skip: !open || !meter,
    fetchPolicy: 'no-cache',
  });

  const [saveBookings, { loading: saving }] = useMutation(SAVE_ESTIMATE_BOOKINGS);

  const meterMatch = useMemo(() => {
    if (!metersData?.metersRequiringEstimates || !meter) return null;
    const groups: MeterGroup[] = metersData.metersRequiringEstimates;
    for (const group of groups) {
      const found = group.meters.find(
        (m) => String(m.meterId) === String(meter.meter_id) || m.meterNumber === meter.meter_number,
      );
      if (found) {
        const [energySource, region] = group.groupKey.split('/');
        return { meter: found, energySource, region };
      }
    }
    return null;
  }, [metersData, meter]);

  const regionalData: RegionalRow[] = useMemo(() => {
    if (!factorsData?.estimationFactors?.data || !meterMatch) return [];
    const rows: EstimationFactorRow[] = factorsData.estimationFactors.data;
    return rows
      .filter((r) => r.energy_source?.toLowerCase() === meterMatch.energySource?.toLowerCase())
      .map((r) => ({
        region: r.region,
        lastMonth: r.prev_month_net_units ?? 0,
        currentMonth: r.current_month_net_units ?? 0,
        factor: r.estimate_factor,
      }));
  }, [factorsData, meterMatch]);

  const handleApply = async (values: { booked: number; comment: string; isManualOverride: boolean }) => {
    if (!meter) {
      setApplyError('No meter selected.');
      return false;
    }
    setApplyError(null);

    // Matched → update existing row; not matched → backend will insert a new row
    const meterId = meterMatch ? Number(meterMatch.meter.meterId) : Number(meter.meter_id);

    try {
      const { data } = await saveBookings({
        variables: {
          updates: [
            {
              meterId,
              month_year: monthYear,
              booked: values.booked,
              comment: values.comment,
              isManualOverride: values.isManualOverride,
            },
          ],
        },
      });

      const result = data?.saveEstimateBookings;
      if (result?.success) {
        onSuccess?.();
        return true;
      } else {
        const itemError = result?.results?.[0]?.error;
        setApplyError(itemError || result?.message || 'Failed to apply estimate');
        return false;
      }
    } catch (err: any) {
      setApplyError(err.message || 'Failed to apply estimate');
      return false;
    }
  };

  return {
    meterData: meterMatch?.meter ?? null,
    energySource: meterMatch?.energySource ?? null,
    regionalData,
    saving,
    applyError,
    handleApply,
  };
}
